// GobMgr — gid allocation (tagged free list), the master gob list (prepend-on-add), the
// per-tile gidMap chains with shadow/end-marker semantics, trigger areas, and spatial
// queries. game/GameObjects.cpp:1283-2300+2739 + game/ht.h:6140-6276.
//
// Gid assignment ORDER is hash-load-bearing ('ID  '/'GNXT' entries): the free list
// allocates from the HEAD and frees to the TAIL, exactly like the C++ tagged-pointer
// scheme. Slots here are indices: a used slot holds the Gob, a free slot holds the NEXT
// free slot's index (the C++ stores a tagged pointer), and the chain's last slot holds
// null (the C++ NULL terminator).

import type { SimContext } from '../context.ts';
import { kgidNull } from '../sm/message.ts';
import { TcFromWc } from '../math/coords.ts';
import { Enum, GetSideMask, TRect, kEnmFirst } from '../types.ts';
import { Gob, kfGobActive, kfGobUnit } from './gob.ts';
import { kgtScenery, kgtScorch } from './gobtypes.ts';
import { ksideNeutral } from '../player/side.ts';
import { kttArea } from '../terrain/terrainmap.ts';
import { kumStructures, kumMobileUnits } from '../player/masks.ts';
import { kfPlrComputer } from '../player/side.ts';
import { kfUntcStructureBuilder, kfUntcMobileUnitBuilder } from './unitconsts.ts';
import type { Player } from '../player/player.ts';

/** The BuilderGob slice the limit checks read (builder.ts can't be imported here: its
 *  class-extends chain would evaluate before StructGob initializes — ESM cycle). */
interface BuilderGobLike {
  m_bq: { GetUnitCount(ut: number): number };
}

// game/Builder.cpp:108-125 — total queued across one CLASS of player (SP limits)
export function GetGlobalQueuedCount(ctx: SimContext, pplr: Player, wfTest: number): number {
  let cQueued = 0;
  const wfPlr = pplr.GetFlags() & kfPlrComputer;
  for (let pgobT = ctx.gobm.GetFirstGob(); pgobT !== null; pgobT = ctx.gobm.GetNextGob(pgobT)) {
    if (!(pgobT.GetFlags() & kfGobUnit)) continue;
    const puntT = pgobT as unknown as UnitGobLike & BuilderGobLike & { GetOwner(): Player };
    if ((puntT.GetOwner().GetFlags() & kfPlrComputer) !== wfPlr) continue;
    if (!(puntT.GetConsts().wf! & wfTest)) continue;
    cQueued += puntT.m_bq.GetUnitCount(-1 /* kutNone */);
  }
  return cQueued;
}

// game/Builder.cpp:127-143 — total queued for ONE player (MP limits)
export function GetQueuedCount(ctx: SimContext, pplr: Player, wfTest: number): number {
  let cQueued = 0;
  for (let pgobT = ctx.gobm.GetFirstGob(); pgobT !== null; pgobT = ctx.gobm.GetNextGob(pgobT)) {
    if (!(pgobT.GetFlags() & kfGobUnit)) continue;
    const puntT = pgobT as unknown as UnitGobLike & BuilderGobLike & { GetOwner(): Player };
    if (puntT.GetOwner() !== pplr) continue;
    if (!(puntT.GetConsts().wf! & wfTest)) continue;
    cQueued += puntT.m_bq.GetUnitCount(-1 /* kutNone */);
  }
  return cQueued;
}

// game/ht.h:6138
export const kwfGidEndMarker = 0x8000;

// game/ht.h:6142
export const kcAreasMax = 32;
export const knAreaLastDiscovery = -1;

// game/ht.h:6167-6171
export const knLimitStruct = 0;
export const knLimitMobileUnit = 1;
export const knLimitScenery = 2;
export const knLimitScorch = 3;
export const knLimitSupport = 4;

// game/game.cpp:487-489 + 505-510 (assigned at game.cpp:526-537)
export const gcSceneryGobsLimit = 100;
export const gcScorchGobsLimit = 30;
export const gcSupportGobsLimit = 50;
export const gcStructGobsLimitMP = 55; // kcStructGobsMaxMP (game.cpp:518)
export const gcMuntGobsLimitMP = 88; // kcMuntGobsMaxMP (game.cpp:519)
export const gcStructGobsHumanLimitSP = 55; // kcStructGobsHumanMaxSP
export const gcMuntGobsHumanLimitSP = 88; // kcMuntGobsHumanMaxSP
export const gcStructGobsComputerLimitSP = 72; // kcStructGobsComputerMaxSP
export const gcMuntGobsComputerLimitSP = 117; // kcMuntGobsComputerMaxSP

// game/GameObjects.cpp:1927
const kcareGrow = 32;

interface AreaEntry {
  iareNext: number;
  gid: number;
} // ht.h:6149-6153

class Area {
  // ht.h:6155-6165
  trc = new TRect();
  iareFree = 0xffff; // (word)-1
  iareHead = 0xffff;
  careAlloc = 0;
  careUsed = 0;
  sidm = 0;
  um = 0;
  aare: AreaEntry[] | null = null;
}

/** The slice of UnitGob the area bookkeeping reads (full UnitGob ports with Unit.cpp). */
interface UnitGobLike {
  GetConsts(): { um: number; wf?: number };
  GetSide(): number;
}

type Slot = Gob | number | null; // used | tagged-free(next index) | terminator

export class GobMgr {
  private ctx: SimContext;

  private m_pgobHead: Gob | null = null;
  private m_pgidMap: Uint16Array | null = null;

  /** RENDER-ONLY view of the per-tile gid map (the client FindGobs walk reads
   *  it like the C++ pointer walk); nothing sim-side consumes this accessor. */
  gidMapForRender(): { map: Uint16Array; ctx: number; cty: number } {
    return { map: this.m_pgidMap!, ctx: this.m_ctx, cty: this.m_cty };
  }
  private m_cpgobMax = 0;
  private m_cpgobActive = 0;
  private m_apgobMaster: Slot[] = [];
  private m_ppgobFreeHead = 0; // slot index (the C++ keeps Gob** pointers)
  private m_ppgobFreeTail = 0;
  /** issue #96 B23: the saved free-list order from a COMPLETE snapshot, applied by
   *  ApplyLoadedFreeList() after the active slots hold their loaded gobs. */
  private m_loadedFreeOrder: number[] | null = null;
  /** issue #96: the saved per-tile gidMap (raw cells + per-gob chain links) from a COMPLETE
   *  snapshot, applied by ApplyLoadedGidMap(). The chain ORDER reflects move history (which
   *  unit last entered a tile), which AddGob's creation-order rebuild cannot reproduce — and
   *  discovery (Unit.cpp NotifyEnemyNearby) walks it, so it must be carried verbatim. */
  private m_loadedGidMap: { cells: [number, number][]; links: [number, number][] } | null = null;
  private m_ctx = 0; // TCoord map width
  private m_cty = 0;
  private m_car = 0;
  private m_aar: Area[] = [];
  private m_cSceneryGobs = 0;
  private m_cScorchGobs = 0;
  private m_cSupportGobs = 0;

  constructor(ctx: SimContext) {
    // game/GameObjects.cpp:1292-1304
    this.ctx = ctx;
    for (let i = 0; i < kcAreasMax; i++) this.m_aar.push(new Area());
  }

  private IgidFromWXY(wx: number, wy: number): number {
    // :1290
    return TcFromWc(wy) * this.m_ctx + TcFromWc(wx);
  }

  // game/GameObjects.cpp:1314-1387
  Init(ctx: number, cty: number, cpgobMax: number): boolean {
    this.m_ctx = ctx;
    this.m_cty = cty;
    this.m_cpgobMax = cpgobMax;

    // Free list: slot i -> i+1, terminator slot at cpgobMax holds null
    this.m_apgobMaster = new Array<Slot>(cpgobMax + 1);
    for (let i = 0; i < cpgobMax; i++) this.m_apgobMaster[i] = i + 1;
    this.m_apgobMaster[cpgobMax] = null;
    this.m_ppgobFreeHead = 0;
    this.m_ppgobFreeTail = cpgobMax;

    this.m_pgidMap = new Uint16Array(ctx * cty).fill(kgidNull);
    return true;
  }

  // game/GameObjects.cpp:1438-1475
  Reset(): void {
    this.m_apgobMaster = [];
    this.m_cpgobActive = 0;
    this.m_pgidMap = null;
    this.m_pgobHead = null; // gobs are GC'd (the C++ deletes the chain)
    this.FreeAreas();
    this.m_cSceneryGobs = 0;
    this.m_cScorchGobs = 0;
    this.m_cSupportGobs = 0;
  }

  // game/GameObjects.cpp:1477-1565 — alloc from the free-list HEAD, or pull a SPECIFIC
  // gid out of the chain (saved-game load), maintaining the tail.
  AddGob(pgob: Gob, gid: number = kgidNull): void {
    if (this.m_cpgobActive >= this.m_cpgobMax) return;

    if (gid === kgidNull) {
      const slot = this.m_ppgobFreeHead;
      const next = this.m_apgobMaster[slot];
      // (*m_ppgobFreeHead != NULL asserted in C++ — non-empty checked via cpgobActive)
      this.m_ppgobFreeHead = next as number;
      this.m_apgobMaster[slot] = pgob;
      gid = slot;
    } else {
      let fFound = false;
      if (this.m_ppgobFreeHead === gid) {
        fFound = true;
        this.m_ppgobFreeHead = this.m_apgobMaster[gid] as number;
      } else {
        // find the free slot whose next-index is `gid`; splice it out
        let p = this.m_ppgobFreeHead;
        while (this.m_apgobMaster[p] !== null) {
          const next = this.m_apgobMaster[p] as number;
          if (next === gid) {
            this.m_apgobMaster[p] = this.m_apgobMaster[gid]; // copy tagged next (or null)
            if (this.m_ppgobFreeTail === gid) this.m_ppgobFreeTail = p;
            fFound = true;
            break;
          }
          p = next;
        }
      }
      if (!fFound) return;
      this.m_apgobMaster[gid] = pgob;
    }

    pgob.m_gid = gid;
    pgob.m_pgobNext = this.m_pgobHead; // prepend: iteration order = reverse insertion
    this.m_pgobHead = pgob;
    this.m_cpgobActive++;

    this.MoveGob(pgob, -1, -1, pgob.m_wx, pgob.m_wy);
    this.TrackGobCounts(pgob, true);
  }

  // game/GameObjects.cpp:1567-1621 — freed slots go to the END of the free list
  RemoveGob(pgob: Gob): void {
    // GameObjects.cpp:1583 pgob->Invalidate() — render-only, host-seamed: the client
    // damages the gob's last-painted bounds (fire-and-forget; no-op headless)
    this.ctx.host.GobInvalidate(pgob);
    this.MoveGob(pgob, pgob.m_wx, pgob.m_wy, -1, -1);

    const slot = pgob.m_gid;
    pgob.m_gid = kgidNull;
    this.m_apgobMaster[slot] = null; // new terminator
    this.m_apgobMaster[this.m_ppgobFreeTail] = slot; // old tail -> freed slot (tagged)
    this.m_ppgobFreeTail = slot;

    // unlink from the master list (pointer-to-pointer walk)
    if (this.m_pgobHead === pgob) {
      this.m_pgobHead = pgob.m_pgobNext;
      pgob.m_pgobNext = null;
    } else {
      for (let p = this.m_pgobHead; p !== null; p = p.m_pgobNext) {
        if (p.m_pgobNext === pgob) {
          p.m_pgobNext = pgob.m_pgobNext;
          pgob.m_pgobNext = null;
          break;
        }
      }
    }

    this.m_cpgobActive--;
    this.TrackGobCounts(pgob, false);
  }

  // game/GameObjects.cpp:1623-1640 (the DEBUG count validation is debug-only)
  TrackGobCounts(pgob: Gob, fIncrement: boolean): void {
    if (!(pgob.GetFlags() & kfGobUnit)) {
      const nDir = fIncrement ? 1 : -1;
      switch (pgob.GetType()) {
        case kgtScenery:
          this.m_cSceneryGobs += nDir;
          break;
        case kgtScorch:
          this.m_cScorchGobs += nDir;
          break;
        default:
          this.m_cSupportGobs += nDir;
          break;
      }
    }
  }

  // game/GameObjects.cpp:1677-1730 — the bedrock unit-count limits. The SP arms add the
  // per-level computer deltas (ggame.CalcUnitCountDeltas — ctx fields, level-bound).
  IsBelowLimit(nLimit: number, pplr?: Player): boolean {
    switch (nLimit) {
      case knLimitStruct: {
        if (!this.ctx.fMultiplayer) {
          const cQueued = GetGlobalQueuedCount(this.ctx, pplr!, kfUntcStructureBuilder);
          if (pplr!.GetFlags() & kfPlrComputer) {
            const cStructGobsComputer = this.ctx.plrm.GetUnitInstanceCountFromMask(
              kumStructures,
              kfPlrComputer,
            );
            return (
              cStructGobsComputer + cQueued <
              gcStructGobsComputerLimitSP + this.ctx.cStructGobsComputerDeltaSP
            );
          }
          const cStructGobsHuman = this.ctx.plrm.GetUnitInstanceCountFromMask(kumStructures, 0);
          return (
            cStructGobsHuman + cQueued < gcStructGobsHumanLimitSP + this.ctx.cStructGobsHumanDeltaSP
          );
        }
        const cQueuedMP = GetQueuedCount(this.ctx, pplr!, kfUntcStructureBuilder);
        return pplr!.GetUnitInstanceCountFromMask(kumStructures) + cQueuedMP < gcStructGobsLimitMP;
      }

      case knLimitMobileUnit: {
        if (!this.ctx.fMultiplayer) {
          const cQueued = GetGlobalQueuedCount(this.ctx, pplr!, kfUntcMobileUnitBuilder);
          if (pplr!.GetFlags() & kfPlrComputer) {
            const cMuntGobsComputer = this.ctx.plrm.GetUnitInstanceCountFromMask(
              kumMobileUnits,
              kfPlrComputer,
            );
            return cMuntGobsComputer + cQueued < gcMuntGobsComputerLimitSP;
          }
          const cMuntGobsHuman = this.ctx.plrm.GetUnitInstanceCountFromMask(kumMobileUnits, 0);
          return cMuntGobsHuman + cQueued < gcMuntGobsHumanLimitSP;
        }
        const cQueuedMP = GetQueuedCount(this.ctx, pplr!, kfUntcMobileUnitBuilder);
        return pplr!.GetUnitInstanceCountFromMask(kumMobileUnits) + cQueuedMP < gcMuntGobsLimitMP;
      }

      case knLimitScenery:
        return this.m_cSceneryGobs < gcSceneryGobsLimit;
      case knLimitScorch:
        return this.m_cScorchGobs < gcScorchGobsLimit;
      case knLimitSupport:
        return this.m_cSupportGobs < gcSupportGobsLimit;
    }
    return false; // C++ asserts
  }

  // game/GameObjects.cpp:1870-1879
  CalcAreaMaskRect(tx: number, ty: number, ctx: number, cty: number): number {
    let am = 0;
    for (let tyT = ty; tyT < ty + cty; tyT++) {
      for (let txT = tx; txT < tx + ctx; txT++) {
        am = (am | this.CalcAreaMask(txT, tyT)) >>> 0;
      }
    }
    return am;
  }

  // game/GameObjects.cpp:1884-1896
  CalcAreaMask(tx: number, ty: number): number {
    if (this.ctx.trmap!.GetTerrainType(tx, ty) !== kttArea) return 0;
    let am = 0;
    for (let nArea = 0; nArea < this.m_car; nArea++) {
      if (this.m_aar[nArea].trc.PtIn(tx, ty)) am = (am | (1 << nArea)) >>> 0;
    }
    return am;
  }

  // game/GameObjects.cpp:1901-1913
  GetAreasFromMask(am: number, anArea: number[]): number {
    let cArea = 0;
    for (let n = 0; n < kcAreasMax && am !== 0; n++) {
      if (am & (1 << n)) {
        anArea[cArea++] = n;
        am = (am & (am - 1)) >>> 0;
      }
    }
    return cArea;
  }

  // game/GameObjects.cpp:2135-2151
  GetAreaRect(nArea: number, side: number = ksideNeutral): TRect {
    const ptrc = new TRect();
    if (side === ksideNeutral || nArea >= 0) {
      // The C++ only Assert'd the range; a release build read m_aar[-1] garbage and played on —
      // reachable from real content via a spawn UnitGroup with no SpawnArea property (quirk C7).
      // TS would throw here, so hand back the empty rect instead.
      if (nArea >= 0 && nArea < this.m_aar.length) {
        const trc = this.m_aar[nArea].trc;
        ptrc.left = trc.left;
        ptrc.top = trc.top;
        ptrc.right = trc.right;
        ptrc.bottom = trc.bottom;
      }
    } else if (nArea === knAreaLastDiscovery) {
      const tpt = this.ctx.plrm.GetPlayer(side)!.GetDiscoverPoint();
      ptrc.left = tpt.tx;
      ptrc.top = tpt.ty;
      ptrc.right = tpt.tx + 1;
      ptrc.bottom = tpt.ty + 1;
    }
    return ptrc;
  }

  // game/GameObjects.cpp:1732-1786 — the gidMap tile chains. Verbatim asymmetry: the
  // head-removal case does NOT clear m_gidNext; the chain-removal case does.
  MoveGob(pgob: Gob, wxOld: number, wyOld: number, wxNew: number, wyNew: number): boolean {
    const map = this.m_pgidMap!;

    let igidOld = -1;
    if (wxOld !== -1 && wyOld !== -1) igidOld = this.IgidFromWXY(wxOld, wyOld);
    let igidNew = -1;
    if (wxNew !== -1 && wyNew !== -1) igidNew = this.IgidFromWXY(wxNew, wyNew);

    if (igidOld === igidNew) return false;

    if (igidOld !== -1) {
      if (map[igidOld] === pgob.m_gid) {
        map[igidOld] = pgob.m_gidNext;
      } else {
        // walk the chain values; track who HOLDS the current value
        let holder: Gob | null = null; // null => the map cell
        let val = map[igidOld];
        while ((val & kwfGidEndMarker) === 0) {
          if (val === pgob.m_gid) {
            if (holder === null) map[igidOld] = pgob.m_gidNext;
            else holder.m_gidNext = pgob.m_gidNext;
            pgob.m_gidNext = kgidNull;
            break;
          }
          holder = this.m_apgobMaster[val] as Gob;
          val = holder.m_gidNext;
        }
      }
    }

    if (igidNew !== -1) {
      pgob.m_gidNext = map[igidNew];
      map[igidNew] = pgob.m_gid;
    }

    return true;
  }

  // game/GameObjects.cpp:2192-2221
  GetGob(gid: number, fActiveOnly = true): Gob | null {
    if (gid & kwfGidEndMarker) return null; // also catches kgidNull (0xffff)
    const slot = this.m_apgobMaster[gid];
    if (slot === null || slot === undefined || typeof slot === 'number') return null;
    if (fActiveOnly && (slot.GetFlags() & kfGobActive) === 0) return null;
    return slot;
  }

  // game/GameObjects.cpp:1788-1805
  GetUnitGob(tx: number, ty: number): Gob | null {
    let gid = this.m_pgidMap![ty * this.m_ctx + tx];
    while (true) {
      if (gid === kgidNull) return null;
      const pgob = this.GetGob(gid & ~kwfGidEndMarker, false);
      if (pgob === null) return null;
      if (pgob.GetFlags() & kfGobUnit) return pgob;
      if (gid & kwfGidEndMarker) return null;
      gid = pgob.m_gidNext;
    }
  }

  // game/GameObjects.cpp:1807-1823
  GetShadowGob(tx: number, ty: number): Gob | null {
    let gid = this.m_pgidMap![ty * this.m_ctx + tx];
    while (true) {
      if (gid === kgidNull) return null;
      if ((gid & kwfGidEndMarker) !== 0) return this.GetGob(gid & ~kwfGidEndMarker, false);
      const pgob = this.GetGob(gid, false);
      if (pgob === null) return null;
      gid = pgob.m_gidNext;
    }
  }

  // game/GameObjects.cpp:1825-1845 — store the gid "inside" the end-of-list marker
  ShadowGob(pgob: Gob, tx: number, ty: number, ctx: number, cty: number): void {
    const map = this.m_pgidMap!;
    for (let tyT = ty; tyT < ty + cty; tyT++) {
      for (let txT = tx; txT < tx + ctx; txT++) {
        const icell = tyT * this.m_ctx + txT;
        if ((map[icell] & kwfGidEndMarker) !== 0) {
          map[icell] = (pgob.m_gid | kwfGidEndMarker) & 0xffff;
        } else {
          let holder = this.m_apgobMaster[map[icell]] as Gob;
          while ((holder.m_gidNext & kwfGidEndMarker) === 0) {
            holder = this.m_apgobMaster[holder.m_gidNext] as Gob;
          }
          holder.m_gidNext = (pgob.m_gid | kwfGidEndMarker) & 0xffff;
        }
      }
    }
  }

  // game/GameObjects.cpp:1847-1869
  UnshadowGob(pgob: Gob, tx: number, ty: number, ctx: number, cty: number): void {
    const map = this.m_pgidMap!;
    for (let tyT = ty; tyT < ty + cty; tyT++) {
      for (let txT = tx; txT < tx + ctx; txT++) {
        const icell = tyT * this.m_ctx + txT;
        if ((map[icell] & kwfGidEndMarker) !== 0) {
          map[icell] = kgidNull;
        } else {
          let holder = this.m_apgobMaster[map[icell]] as Gob;
          while ((holder.m_gidNext & kwfGidEndMarker) === 0) {
            holder = this.m_apgobMaster[holder.m_gidNext] as Gob;
          }
          if ((holder.m_gidNext & ~kwfGidEndMarker) === pgob.m_gid) {
            holder.m_gidNext = kgidNull;
          }
        }
      }
    }
  }

  // ht.h:6209-6244 inlines
  GetFirstGob(): Gob | null {
    return this.m_pgobHead;
  }
  GetNextGob(pgob: Gob): Gob | null {
    return pgob.m_pgobNext;
  }
  GetFirstGid(tx: number, ty: number): number {
    const gid = this.m_pgidMap![ty * this.m_ctx + tx];
    if (gid & kwfGidEndMarker) return kgidNull;
    return gid;
  }
  GetNextGid(gid: number): number {
    const pgob = this.GetGob(gid, false);
    if (pgob === null) return kgidNull;
    return pgob.m_gidNext;
  }
  GetMapSize(): { ctx: number; cty: number } {
    return { ctx: this.m_ctx, cty: this.m_cty };
  }
  GetGobCount(): number {
    return this.m_cpgobActive;
  }

  // game/GameObjects.cpp:2739-2800 — the TRect spatial query (sim path; the Rect/fog
  // overload is render-only and excluded). -2/+2 x, -3/+1 y expansion per max gob bounds.
  FindGobsT(ptrcBounds: TRect, apgob: Gob[], cpgobMax: number): number {
    const map = this.m_pgidMap!;
    const txLeft = Math.max(0, ptrcBounds.left - 2);
    const txRight = Math.min(this.m_ctx - 1, ptrcBounds.right + 2);
    const tyTop = Math.max(0, ptrcBounds.top - 3);
    const tyBottom = Math.min(this.m_cty - 1, ptrcBounds.bottom + 1);

    const ctx = txRight - txLeft;
    let cpgob = 0;
    const trcClip = new TRect();
    for (let ty = tyTop; ty <= tyBottom; ty++) {
      const rowBase = ty * this.m_ctx + txLeft;
      for (let i = 0; i <= ctx; i++) {
        let gidT = map[rowBase + i];
        while ((gidT & kwfGidEndMarker) === 0) {
          const pgobT = this.m_apgobMaster[gidT] as Gob;
          gidT = pgobT.m_gidNext;
          if ((pgobT.GetFlags() & kfGobActive) === 0) continue;
          pgobT.GetTileRect(trcClip);
          if (trcClip.left >= ptrcBounds.right) continue;
          if (trcClip.right <= ptrcBounds.left) continue;
          if (trcClip.top >= ptrcBounds.bottom) continue;
          if (trcClip.bottom <= ptrcBounds.top) continue;
          apgob[cpgob++] = pgobT;
          if (cpgob === cpgobMax) return cpgob;
        }
      }
    }
    return cpgob;
  }

  // ---- areas (trigger system) ---------------------------------------------------
  // (GetAreasFromMask is defined once above — GameObjects.cpp:1901; a duplicate
  // private copy here would silently shadow it under JS class semantics)

  // game/GameObjects.cpp:1915-1925
  MoveGobBetweenAreas(gid: number, amOld: number, amNew: number): void {
    if (amOld === amNew) return;
    const amRemove = (amOld & ~amNew) >>> 0;
    if (amRemove !== 0) this.RemoveGobFromAreas(gid, amRemove);
    const amAdd = (amNew & ~amOld) >>> 0;
    if (amAdd !== 0) this.AddGobToAreas(gid, amAdd);
  }

  // game/GameObjects.cpp:1929-2003
  private AddGobToAreas(gid: number, am: number): void {
    const aiar: number[] = [];
    const car = this.GetAreasFromMask(am, aiar);

    for (let iar = 0; iar < car; iar++) {
      const par = this.m_aar[aiar[iar]];
      if (par.iareFree === 0xffff) {
        const careNew = par.careAlloc + kcareGrow;
        const aare: AreaEntry[] = new Array(careNew);
        for (let i = 0; i < par.careAlloc; i++) aare[i] = par.aare![i];
        for (let i = par.careAlloc; i < careNew; i++) aare[i] = { iareNext: 0, gid: 0 };
        par.aare = aare;
        par.iareFree = par.careAlloc;
        for (let iareNew = par.iareFree; iareNew < careNew - 1; iareNew++) {
          par.aare[iareNew].iareNext = iareNew + 1;
        }
        par.aare[careNew - 1].iareNext = 0xffff;
        par.careAlloc = careNew;
      }

      const iareNew = par.iareFree;
      par.iareFree = par.aare![iareNew].iareNext;
      par.aare![iareNew].iareNext = par.iareHead;
      par.iareHead = iareNew;
      par.careUsed++;
      par.aare![iareNew].gid = gid;

      const punt = this.m_apgobMaster[gid] as Gob;
      if (punt !== null && punt.m_ff & kfGobUnit) {
        par.sidm |= GetSideMask(punt.GetSide());
        par.um = (par.um | (punt as unknown as UnitGobLike).GetConsts().um) >>> 0;
      }
    }
  }

  // game/GameObjects.cpp:2005-2068 — removal RECALCULATES sidm/um from survivors
  private RemoveGobFromAreas(gid: number, am: number): void {
    const aiar: number[] = [];
    const car = this.GetAreasFromMask(am, aiar);

    for (let iar = 0; iar < car; iar++) {
      const par = this.m_aar[aiar[iar]];
      par.sidm = 0;
      par.um = 0;

      // word *piareNext walk: holder is either the area head or an entry's iareNext
      let holderEntry: AreaEntry | null = null; // null => par.iareHead
      let val = par.iareHead;
      while (val !== 0xffff) {
        const pare = par.aare![val];
        if (pare.gid === gid) {
          const iareT = val;
          val = pare.iareNext;
          if (holderEntry === null) par.iareHead = pare.iareNext;
          else holderEntry.iareNext = pare.iareNext;
          pare.iareNext = par.iareFree;
          par.iareFree = iareT;
          par.careUsed--;
        } else {
          const punt = this.m_apgobMaster[pare.gid] as Gob;
          if (punt !== null && punt.m_ff & kfGobUnit) {
            par.sidm |= GetSideMask(punt.GetSide());
            par.um = (par.um | (punt as unknown as UnitGobLike).GetConsts().um) >>> 0;
          }
          holderEntry = pare;
          val = pare.iareNext;
        }
      }
    }
  }

  // game/GameObjects.cpp:2070-2112
  EnumGobsInArea(penm: Enum, nArea: number, sidm: number, um: number): Gob | null {
    const par = this.m_aar[nArea];
    if (penm.m_dwUser === kEnmFirst) {
      if (!(par.sidm & sidm)) return null;
      if (!((par.um & um) >>> 0)) return null;
      penm.m_dwUser = 0;
      penm.m_wUser = par.iareHead;
    }

    while (penm.m_wUser !== 0xffff) {
      const pare = par.aare![penm.m_wUser];
      penm.m_wUser = pare.iareNext;

      const pgobT = this.m_apgobMaster[pare.gid] as Gob;
      if (!(pgobT.GetFlags() & kfGobUnit)) continue;
      const puntT = pgobT as unknown as UnitGobLike;
      if (!(GetSideMask(puntT.GetSide()) & sidm)) continue;
      if (!((puntT.GetConsts().um & um) >>> 0)) continue;
      return pgobT;
    }
    return null;
  }

  // game/GameObjects.cpp:2114-2125
  CheckUnitsInArea(nArea: number, sidm: number, um: number): boolean {
    const par = this.m_aar[nArea];
    if (!(par.sidm & sidm)) return false;
    if (!((par.um & um) >>> 0)) return false;
    return true;
  }

  // game/GameObjects.cpp:2127-2133
  IsGobWithinArea(pgobTarget: Gob, nArea: number): boolean {
    const tpt = { tx: 0, ty: 0 };
    pgobTarget.GetTilePosition(tpt);
    return this.m_aar[nArea].trc.PtIn(tpt.tx, tpt.ty);
  }

  // game/GameObjects.cpp:2153-2181 — the IniReader parse becomes a JSON-input boundary;
  // entries arrive pre-parsed as "left,top,width,height" tuples in [Areas] file order.
  LoadAreas(areas: { left: number; top: number; width: number; height: number }[]): boolean {
    for (let iar = 0; iar < kcAreasMax; iar++) {
      const par = this.m_aar[iar];
      par.trc = new TRect();
      par.iareFree = 0xffff;
      par.iareHead = 0xffff;
      par.careAlloc = 0;
      par.careUsed = 0;
      par.sidm = 0;
      par.um = 0;
      par.aare = null;
    }
    this.m_car = 0;
    for (const a of areas) {
      this.m_aar[this.m_car].trc.Set(a.left, a.top, a.left + a.width, a.top + a.height);
      this.m_car++;
    }
    return true;
  }

  // game/GameObjects.cpp:2183-2190
  private FreeAreas(): void {
    for (let iar = 0; iar < this.m_car; iar++) this.m_aar[iar].aare = null;
    this.m_car = 0;
  }

  get carAreas(): number {
    return this.m_car;
  }

  // game/GameObjects.cpp:1389-1442 (knVerGobMgr 5) — the C++ writes each Area as a RAW
  // 28-byte struct image (wasm32 layout: TRect 4xs16, four words, sidm word, 2 pad
  // bytes, um dword, the aare HEAP POINTER as a dword — only its NULL-ness matters on
  // load) followed by the raw AreaEntry[careAlloc] (iareNext word + gid word each).
  SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(5);
    pstm.WriteWord(this.m_car);
    for (let iar = 0; iar < this.m_car; iar++) {
      const par = this.m_aar[iar];
      // TRect derives from Rect whose fields are int (ht.h:566) — the raw struct image
      // carries 32-bit rect fields on wasm32 (verified byte-for-byte vs the real client
      // by auto/savegame-xray.mjs; the first TS port wrongly assumed 16-bit TCoords).
      pstm.WriteDword(par.trc.left);
      pstm.WriteDword(par.trc.top);
      pstm.WriteDword(par.trc.right);
      pstm.WriteDword(par.trc.bottom);
      pstm.WriteWord(par.iareFree);
      pstm.WriteWord(par.iareHead);
      pstm.WriteWord(par.careAlloc);
      pstm.WriteWord(par.careUsed);
      pstm.WriteWord(par.sidm);
      pstm.WriteWord(0); // struct padding before the dword-aligned um
      pstm.WriteDword(par.um);
      pstm.WriteDword(par.aare !== null ? 1 : 0); // the raw pointer; NULL-ness is the signal
      if (par.aare !== null) {
        for (let i = 0; i < par.careAlloc; i++) {
          pstm.WriteWord(par.aare[i].iareNext);
          pstm.WriteWord(par.aare[i].gid);
        }
      }
    }
    // issue #96 B23 (snapshotComplete only): append the free-list ORDER (head→tail). The
    // C++/faithful path never saves it, so a loaded game rebuilt the chain in the carve-on-
    // load order (not the saver's alloc/free history) and allocated different gids for the
    // first gob spawned after load → divergence. Persisting it makes resume allocations
    // bit-exact. The version byte stays 5 (faithful saves are byte-unchanged).
    if (this.ctx.snapshotComplete) {
      const freeOrder: number[] = [];
      for (let p = this.m_ppgobFreeHead; ;) {
        freeOrder.push(p);
        const next = this.m_apgobMaster[p];
        if (next === null) break; // the terminator slot (m_ppgobFreeTail)
        p = next as number;
      }
      pstm.WriteWord(freeOrder.length);
      for (const idx of freeOrder) pstm.WriteWord(idx);

      // issue #96 / #98: the per-tile gidMap chain ORDER (cells + per-gob m_gidNext links), restricted
      // to SAVABLE gobs. Discovery (Unit.cpp NotifyEnemyNearby) walks GetFirstGid/GetNextGid, so the
      // per-tile order is sim-affecting and move-history-dependent (AddGob's creation-order rebuild
      // scrambles it). Restricted to gobs the snapshot actually serializes (IsSavable() — which is
      // true for the COMPLETE-snapshot transients here, #98): any gob NOT in the records (e.g. a
      // still-unsavable decal) must be skipped or its gid would dangle on load — a head cell or
      // link → a gid with no gob → MoveGob/RemoveGob walk into `undefined`. Emit savable-only
      // chains, re-stitched past such gobs (shadows are savable structures, end-marked, and kept).
      const map = this.m_pgidMap!;
      // From a chain VALUE, return the first SAVABLE value (gid | end-bit) reachable, else kgidNull.
      // Skips unsavable transients; stops at an end-marker (a shadow — itself a savable structure).
      const firstSavable = (v: number): number => {
        while (v !== kgidNull) {
          const pg = this.GetGob(v & ~kwfGidEndMarker, false);
          if (pg !== null && pg.IsSavable()) return v;
          if ((v & kwfGidEndMarker) !== 0 || pg === null) return kgidNull;
          v = pg.m_gidNext;
        }
        return kgidNull;
      };
      // cells: per tile, re-point the head past any unsavable transient to the first savable value.
      const cells: number[] = [];
      for (let i = 0; i < map.length; i++) {
        if (map[i] === kgidNull) continue;
        const h = firstSavable(map[i]);
        if (h !== kgidNull) cells.push(i, h);
      }
      pstm.WriteDword(cells.length >> 1);
      for (let k = 0; k < cells.length; k += 2) {
        pstm.WriteDword(cells[k]);
        pstm.WriteWord(cells[k + 1]);
      }
      // links: ONE per savable gob (so a shadow structure linked from several tiles isn't written
      // twice), its m_gidNext re-stitched past unsavable transients to the next savable value.
      const links: number[] = [];
      for (let g = this.m_pgobHead; g !== null; g = g.m_pgobNext) {
        if (!g.IsSavable()) continue;
        links.push(g.m_gid, firstSavable(g.m_gidNext));
      }
      pstm.WriteWord(links.length >> 1);
      for (let k = 0; k < links.length; k += 2) {
        pstm.WriteWord(links[k]);
        pstm.WriteWord(links[k + 1]);
      }
    }
    return pstm.IsSuccess();
  }

  // game/GameObjects.cpp:1407-1442
  LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 5) return false;
    this.m_car = pstm.ReadWord();
    for (let iar = 0; iar < this.m_car; iar++) {
      const par = this.m_aar[iar];
      par.trc.Set(
        pstm.ReadDword() | 0,
        pstm.ReadDword() | 0,
        pstm.ReadDword() | 0,
        pstm.ReadDword() | 0,
      );
      par.iareFree = pstm.ReadWord();
      par.iareHead = pstm.ReadWord();
      par.careAlloc = pstm.ReadWord();
      par.careUsed = pstm.ReadWord();
      par.sidm = pstm.ReadWord();
      pstm.ReadWord(); // padding
      par.um = pstm.ReadDword();
      const ptr = pstm.ReadDword();
      if (ptr !== 0) {
        par.aare = new Array(par.careAlloc);
        for (let i = 0; i < par.careAlloc; i++) {
          par.aare[i] = { iareNext: pstm.ReadWord(), gid: pstm.ReadWord() };
        }
      } else {
        par.aare = null;
      }
    }
    // issue #96 B23: a COMPLETE snapshot appended the free-list order; stash it for
    // ApplyLoadedFreeList() (called once the gob records have re-occupied their slots).
    if (this.ctx.snapshotComplete) {
      const cFree = pstm.ReadWord();
      const order = new Array<number>(cFree);
      for (let i = 0; i < cFree; i++) order[i] = pstm.ReadWord();
      this.m_loadedFreeOrder = order;

      // issue #96: stash the gidMap cells + chain links for ApplyLoadedGidMap().
      const cCells = pstm.ReadDword() >>> 0;
      const cells: [number, number][] = [];
      for (let i = 0; i < cCells; i++) cells.push([pstm.ReadDword() >>> 0, pstm.ReadWord()]);
      const cLinks = pstm.ReadWord();
      const links: [number, number][] = [];
      for (let i = 0; i < cLinks; i++) links.push([pstm.ReadWord(), pstm.ReadWord()]);
      this.m_loadedGidMap = { cells, links };
    }
    return pstm.IsSuccess();
  }

  // issue #96 B23: relink the free chain to the saved head→tail order (no-op for a faithful
  // load). Called from Level.LoadState AFTER all gobs are placed — the serialized free
  // indices are exactly the slots NOT holding a loaded gob, so this never clobbers a live gob.
  ApplyLoadedFreeList(): void {
    const order = this.m_loadedFreeOrder;
    if (order === null) return;
    this.m_loadedFreeOrder = null;
    if (order.length === 0) return;
    for (let i = 0; i < order.length - 1; i++) this.m_apgobMaster[order[i]] = order[i + 1];
    this.m_apgobMaster[order[order.length - 1]] = null;
    this.m_ppgobFreeHead = order[0];
    this.m_ppgobFreeTail = order[order.length - 1];
  }

  // issue #96: overwrite the gidMap with the saver's exact per-tile chain order (no-op for a
  // faithful load). AddGob's MoveGob rebuilt it in creation order during the gob loop; here we
  // restore the move-history order so discovery (and any GetFirstGid/GetNextGid walk) matches.
  ApplyLoadedGidMap(): void {
    const saved = this.m_loadedGidMap;
    if (saved === null) return;
    this.m_loadedGidMap = null;
    this.m_pgidMap!.fill(kgidNull);
    for (const [cell, val] of saved.cells) this.m_pgidMap![cell] = val;
    for (const [gid, gidNext] of saved.links) {
      const g = this.m_apgobMaster[gid];
      if (g !== null && typeof g === 'object') g.m_gidNext = gidNext;
    }
  }
}
